export default {
  home: {
    hero: {
      badge: 'Agentic delivery orchestration',
      title: 'AI agents that run your deliveries,',
      titleEnd: 'on rules you set.',
      description: 'Geofleet plans routes, dispatches every order, tracks each stop live and captures proof of delivery. Agents handle the routine work within the guardrails you configure, and your team steps in only when it matters.',
      feedTitle: 'Agents at work',
      feedLive: 'Live',
      feedFoot: 'Sample activity · guardrails you set',
      feedCta: 'See the agents',
      feed: [
        { text: 'Dispatch agent matched ODR-78262 to Amit Kumar', meta: 'Scored 92 · 3.1 km · accepted in 6 s' },
        { text: 'Route recovery replanned 4 stops around a road closure', meta: 'Hebbal · ETA kept within window' },
        { text: 'SLA guardian flagged Stop 12 at risk, 18 min behind', meta: 'Reassignment proposed for approval' },
        { text: 'Customer comms sent new ETAs to 2 customers on WhatsApp', meta: 'ODR-78251 · ODR-78264' },
        { text: 'Proof of delivery captured: photo, signature, OTP', meta: 'ODR-78240 · Priya Nair · 2:14 PM' },
        { text: 'Cash agent reconciled 3 settlements, 1 flagged for review', meta: 'North Zone · ₹1,850 outstanding' },
      ],
      ctaPrimary: 'Start for free',
      ctaSecondary: 'Book a demo',
      microTrust: '7-day free trial on Lite · Plans from $39/month',
      countries: '20+ Countries',
      customers: '100+ Customers',
      orders: '1M+ orders processed',
      industriesHeading: 'Industries we serve',
      trustedBy: 'Trusted by operations teams in 20+ countries',
      imageAlt: 'Geofleet command centre for North Zone: 96% on-time, 24 active drivers, a live map with two delayed stops, and Copilot suggesting to move stops 12 and 14 to a nearby driver with spare capacity',
      phoneTrackingAlt: 'Branded customer tracking page on a phone with live map and driver details',
      phoneDriverAlt: 'Geofleet driver app home screen with shift timer and current trip',
      chipDelta: '+2% vs yesterday', chipCopilot: 'Copilot suggested fix', chipCopilotText: 'Reassign 2 late stops · ETAs back in window',
      chipCustomer: 'Customer notified', chipCustomerText: 'Live ETA sent by SMS & WhatsApp',
      cityAlt: 'Aerial view of a city highway at golden hour with delivery trucks and vans moving along glowing blue routes and numbered stop pins',
      statOnTimeLabel: 'On-time deliveries',
      statOnTimeValue: '96%',
      statCostLabel: 'Lower delivery cost',
      statCostValue: '20–30%',
      statRiderLabel: 'Rider utilization',
      statRiderValue: '+22%',
      capRoute: 'Route optimization',
      capDispatch: 'Auto-dispatch',
      capTracking: 'Live tracking',
      capPod: 'Proof of delivery',
      trustedLine: 'Trusted by 100+ operations teams in 20+ countries',
      statSource: 'Results from customer case studies',
      announceBadge: 'New',
      announceText: 'Introducing Geofleet Command: AI agents configured to your network',
      announceCta: 'See plans',
      proofSecurity: 'SOC 2 and ISO 27001 certified',
      costLine: 'lower delivery cost, from customer case studies',
      chooser: {
        subtitle: 'Pick the option that matches your operation.',
        close: 'Close',
        title: 'Where do you deliver from?',
        liteTitle: 'One hub, up to 25 drivers',
        liteText: 'Geofleet Lite · 7-day free trial, then from $39/month',
        commandTitle: 'Several hubs or more than 25 drivers',
        commandText: 'Geofleet Command · AI agents and configurable workflows, from $19 per driver',
      },
    },
    proofBar: {
      stats: [
        { value: '100+', label: 'Customers' },
        { value: '20+', label: 'Countries' },
        { value: '95%', label: 'On-time rate' },
        { value: '99.99%', label: 'Uptime' },
        { value: '50+', label: 'Integrations' },
      ],
    },
    exceptionStory: {
      eyebrow: 'Command centre + Copilot',
      title: 'A late driver, handled automatically',
      subtitle: 'Geofleet spots the problem, works out the fix and updates everyone. Your team only steps in to approve.',
      steps: [
        {
          title: 'Detected automatically',
          body: 'The moment a driver falls behind, Geofleet flags the at-risk stops and moves them to the top of the queue. Nobody has to go looking.',
        },
        {
          title: 'Explained, not just alerted',
          body: 'Copilot tells the team what is late and why, such as traffic on the ring road. Anyone can ask a follow-up in plain language.',
        },
        {
          title: 'Fix prepared for you',
          body: 'It finds the nearest driver with spare capacity and prepares the reassignment with new ETAs, ready for one-tap approval or automatic within your guardrails.',
        },
        {
          title: 'Everyone updated automatically',
          body: 'Both drivers’ apps update, customers get a new ETA by SMS or WhatsApp, and the change is logged. No calls, no messages to send.',
        },
      ],
      /** Labels on the screenshot for each step (sample data). */
      annotations: [
        { status: 'Risk detected automatically', detail: 'Stop 12 · 18 min behind' },
        { status: 'Copilot briefing', detail: 'Cause: traffic on the ring road' },
        { status: 'Fix ready to approve', detail: 'Move 2 stops to Amit Kumar' },
        { status: 'Done automatically', detail: 'Customers notified via WhatsApp' },
      ],
      footnote: 'Choose what runs automatically and what waits for approval. Every action is logged.',
      cta: 'See the AI agents',
      /** Caption under the step screenshots, which use illustrative data. */
      mock: { sample: 'Sample data' },
    },
    platformCapabilities: {
      eyebrow: 'Platform',
      title: 'Enterprise platform',
      titleHighlight: 'capabilities',
      subtitle: 'Geofleet plans, dispatches, tracks and proves every delivery from one platform. Pick a capability to see it in the product.',
      explore: 'Explore',
      prev: 'Previous capability',
      next: 'Next capability',
      pause: 'Pause',
      play: 'Play',
    },
    industryImpact: {
      eyebrow: 'Customer stories',
      title: 'Proven impact across',
      titleHighlight: 'every major industry',
      subtitle: 'Restaurants, grocery, pharmacy, parcel networks and 3PLs run on Geofleet. Every story below is a real customer, kept anonymous at their request.',
      challenge: 'The challenge',
      solution: 'The solution',
      readCaseStudy: 'Read the case study',
      industryPage: 'Geofleet for this industry',
      allCaseStudies: 'See all case studies',
    },
    whyGeofleet: {
      eyebrow: 'Why Geofleet',
      title: 'Why delivery teams',
      titleHighlight: 'choose Geofleet',
      subtitle: 'An agent-run command centre on top of the proof, cash and driver tools you already need, with the numbers to back it.',
      items: {
        dispatch: { title: 'Dispatch that runs itself', body: 'An always-on agent matches every order to a driver or carrier in seconds and offers it in the driver app. Dispatchers only see the exceptions.' },
        onTime: { title: 'On-time, every day', body: 'Live ETAs, re-matching on every event and SLA alerts keep routes on schedule when the day stops going to plan.' },
        cost: { title: 'Lower cost per delivery', body: 'Fuller routes, fewer failed attempts and no manual dispatch effort take 20–30% out of the cost of each stop.' },
        reliability: { title: 'Built for scale and audit', body: 'SOC 2 and ISO 27001, 99.99% uptime and a decision trace for every assignment, so operations and finance can both trust the numbers.' },
        integrations: { title: 'Fits the stack you have', body: 'Orders in from your store or ERP, updates out by WhatsApp and SMS, cash reconciled to your books. Sandbox, webhooks and an API for the rest.' },
        global: { title: 'Ready for every market', body: 'Teams in 20+ countries run Geofleet with the dashboard and driver app in four languages and prices shown in local currency.' },
      },
      labels: { order: 'Order', matched: 'Matched', accepted: 'Accepted', onTimeRate: 'On-time rate', lowerCost: 'Lower cost per delivery', manual: 'Manual', uptime: 'Uptime', integrations: 'Integrations', countries: 'Countries' },
    },
    integrationsHub: {
      eyebrow: 'Integrations',
      title: 'Seamlessly connected with',
      titleHighlight: '50+ native integrations',
      subtitle: 'Orders flow in from your store, POS or ERP, updates flow out to customers, and cash settles to your books. Model Context Protocol lets your own AI tools work with the same data.',
      hubLabel: 'Command centre',
      categories: { commerce: 'Commerce & order sources', erp: 'ERP, accounting & CRM', automation: 'Automation & AI', messaging: 'SMS & email', payments: 'Payments & team alerts', maps: 'Maps & carriers' },
      soon: 'Coming soon',
      cta: 'Browse all integrations',
      apiDocs: 'API documentation',
    },
    lifecycle: {
      eyebrow: 'How it works',
      title: 'Six steps from order to proof of delivery',
      cta: 'See how it works',
      scrollHint: 'Scroll to follow one order from import to proof of delivery.',
      /** Status pill and detail chip shown on the visual for each step (sample data). */
      annotations: [
        { status: 'Importing orders', detail: '312 orders · 3 need attention' },
        { status: 'Routes optimized', detail: '4 routes · 312 stops planned' },
        { status: 'Dispatched', detail: 'Route sent to Amit Kumar’s app' },
        { status: 'Live on the map', detail: 'Stop 12 re-sequenced · ETA 11:38 AM' },
        { status: 'Customer notified', detail: 'Tracking link sent via WhatsApp' },
        { status: 'Delivered', detail: 'Photo, signature and OTP captured' },
      ],
      stepShort: ['Import', 'Plan', 'Dispatch', 'Track', 'Notify', 'Prove'],
    },
    industryTabs: {
      eyebrow: 'Built for your industry',
      title: 'One platform, tuned to how you deliver',
      cta: 'Explore {{industry}}',
      all: 'All industries',
    },
    pricingTeaser: {
      eyebrow: 'Pricing',
      title: 'One hub or many—there is a plan built for it',
      subtitle: 'Both start at a monthly price with drivers and orders included. Lite runs one hub on a flat plan; Command scales per driver across many hubs.',
      perDriver: '/driver/mo',
      compare: 'Compare plans',
      startFree: 'Get started',
    },
    problemSection: {
      eyebrow: 'Last-mile reality',
      title: 'Manual Dispatch is Breaking Last-Mile Efficiency',
      subtitle:
        'When assignment lives in chats, calls, and spreadsheets, these outcomes are inevitable—and they compound every peak hour.',
      pains: [
        {
          title: 'Delayed deliveries',
          description:
            'Orders wait for a human decision while traffic, capacity, and SLAs move on—so delays stack and customers churn.',
        },
        {
          title: 'Idle riders',
          description:
            'Drivers sit underutilized in one zone while demand spikes elsewhere because dispatch can’t reallocate fast enough.',
        },
        {
          title: 'Dispatcher overload',
          description:
            'Surges turn your team into a manual switchboard—triage, reassignment, and rework eat hours that should scale.',
        },
        {
          title: 'Inconsistent ETAs',
          description:
            'Promised windows drift from reality when plans aren’t tied to live location, workload, and route state.',
        },
        {
          title: 'Operational inefficiency',
          description:
            'Cost per stop creeps up: extra miles, overtime, and exception handling replace repeatable, optimized execution.',
        },
      ],
    },
    outcomes: {
      eyebrow: 'Outcomes — not feature lists',
      title: 'What changes when dispatch runs in real time',
      subtitle:
        'Teams adopt Geofleet to move KPIs that matter on the ground: fewer late deliveries, fuller routes, and dispatchers focused on exceptions—not spreadsheets.',
      items: [
        {
          headline: '30%',
          trend: 'down',
          title: 'Delivery delays',
          sub: 'Reduction in delay incidents vs. manual assignment baselines.',
        },
        {
          headline: '25%',
          trend: 'up',
          title: 'Rider utilization',
          sub: 'More productive stops per shift when orders match live capacity.',
        },
        {
          headline: '80%',
          trend: 'down',
          title: 'Manual dispatch effort',
          sub: 'Less time on reassignment, calls, and rework during peaks.',
        },
        {
          headline: 'On-time',
          trend: 'up',
          title: 'Delivery rate',
          sub: 'Higher SLA adherence when ETAs reflect live route state.',
        },
      ],
    },
    roiCalculator: {
      eyebrow: 'ROI snapshot',
      title: 'Estimate your monthly savings',
      subtitle:
        'A quick model from your volume, fleet size, and delay economics—refine with our team on your real network.',
      ordersLabel: 'Orders per day',
      ridersLabel: 'Riders / drivers',
      delayCostLabel: 'Avg. delay cost per order ($)',
      ordersHelp: 'Average daily order volume you dispatch.',
      ridersHelp: 'Active riders or drivers in the last-mile fleet.',
      delayCostHelp: 'Blended cost of a late or suboptimal delivery (CX, redelivery, fees).',
      outputLabel: 'Estimated monthly savings',
      breakdownDelays: 'From fewer delays (30%)',
      breakdownUtil: 'From higher utilization (25%)',
      breakdownManual: 'From less manual dispatch (80% of rider-touch overhead)',
      disclaimer:
        'Illustrative only—not financial advice or a guarantee. Actual results depend on network, SLAs, and adoption. Book a demo for a tailored model.',
      cta: 'Book a demo to validate',
      ctaSecondary: 'Talk to sales',
    },
    aiAgents: {
      eyebrow: 'AI agents for logistics',
      title: 'Agents that dispatch, reroute, and resolve—before your team has to',
      intro:
        'Geofleet deploys agentic workflows in your command centre: carriers and drivers are matched in real time, routes replan when traffic or orders change, and SLA-risk is surfaced with context—so scale is not capped by manual dispatch playbooks.',
      cta: 'Explore AI Agents',
    },
    trust: {
      title: 'One command centre. AI agents doing the heavy lifting.',
      subtitle: 'Automate the decisions that used to burn dispatcher hours—without ripping out your stack.',
      benefits: [
        {
          title: 'Agentic dispatch & allocation',
          description:
            'AI agents match every shipment to the right carrier and driver using capacity, cost, SLA, skills, and live conditions—continuously, not overnight batch files.',
        },
        {
          title: 'Routes that replan themselves',
          description:
            'When traffic shifts, attempts fail, or orders land mid-route, agentic route planning adapts so your team is not rebuilding tours in spreadsheets.',
        },
        {
          title: 'Exceptions handled in the tower',
          description:
            'Proactive alerts for SLA risk, delays, and capacity crunches—prioritized by AI so humans fix what matters first while agents clear the long tail.',
        },
        {
          title: 'Visibility executives and customers trust',
          description:
            'Predictive ETAs, branded tracking, and live maps—fed by the same agent-driven model your dispatchers use, so everyone sees one version of the truth.',
        },
      ],
    },
    howItWorks: {
      badge: 'Command centre',
      title: 'One tower where you',
      titleHighlight: 'chat & command',
      subtitle:
        'Everything happens in the command centre: chat with Copilot to plan and fix work, let it run bulk actions for you, and finish rollout with AI-assisted onboarding—no swivel-chair between tools.',
    },
    howItWorksBlocks: {
      pillarsSectionEyebrow: 'How it works',
      pillarsSectionTitle: 'Two surfaces—not one long feature list',
      pillarsIntro:
        'Everything below is split into Command centre (live ops) and Copilot (AI on the same data). Four compact capabilities each—easy to scan.',
      flowSectionTitle: 'How the journey connects',
      flowSectionIntro: 'Tower first, then Copilot, execution, and guided onboarding—same story, step by step.',
      steps: {
        commandCentre: 'Command centre',
        copilot: 'Copilot',
        execute: 'Execute & automate',
        aiOnboarding: 'AI onboarding',
      },
      stepBlurbs: {
        commandCentre: 'Live map, queues, and controls—one surface for the whole operation.',
        copilot: 'Ask in plain language: assign routes, clear exceptions, and pull status.',
        execute: 'Turn answers into bulk updates, reroutes, and workflows without tab-hopping.',
        aiOnboarding: 'Guided setup for integrations, hubs, and roles—Copilot walks your team through it.',
      },
      majorFeatures: [
        {
          kicker: 'Operations',
          title: 'Command centre',
          subtitle: 'One live view of every order, driver and stop, with the metrics and audit trail behind them.',
          cards: [
            {
              title: 'Live map & queues',
              line: 'See delivered, in-transit, and at-risk stops together—exceptions rise to the top.',
            },
            {
              title: 'Connect your stack',
              line: 'Native integrations, MCP for AI tools, CSV bulk loads, or manual master data—drivers, vehicles, hubs, orders.',
            },
            {
              title: 'Plan & change fast',
              line: 'Dispatch, reassign, bulk updates, and manifests where your policy allows.',
            },
            {
              title: 'Insights & audit',
              line: 'KPIs and drill-downs on delays; exports and history for CX, finance, and compliance.',
            },
          ],
        },
        {
          kicker: 'AI automation',
          title: 'Copilot and AI agents',
          subtitle: 'They watch live operations, prepare fixes and act within your guardrails, so routine exceptions resolve themselves.',
          cards: [
            {
              title: 'Detect problems early',
              line: 'Late drivers, failed attempts and SLA risk are flagged the moment they happen.',
            },
            {
              title: 'Prepare the fix',
              line: 'Reassignments, reroutes and customer updates are ready for one-tap approval.',
            },
            {
              title: 'Act within guardrails',
              line: 'Routine moves run on their own; anything outside your limits comes back to a person.',
            },
            {
              title: 'Explain on demand',
              line: 'Ask “what changed since 09:00?” and get answers from live data, not generic AI text.',
            },
          ],
        },
      ],
      seeInAction: 'See it in action',
      howItWorksLink: 'How it works',
      bottomCtaTitle: 'Ready to run deliveries your way?',
      bottomCtaSub: 'Get a single view of CX and operations — no spreadsheets.',
    },
    platformCarousel: {
      badge: 'Command centre',
      title: 'See Geofleet in action',
      subtitle: 'The same surface your agents use—dispatch, live map, routing, and analytics in one place.',
      slides: [
        { title: 'Dispatch Dashboard', description: 'One hub where agents and dispatchers assign, rebalance, and clear exceptions in real time' },
        { title: 'Route Planning', description: 'Agentic routing that reacts to traffic, windows, and new volume—beyond static route files' },
        { title: 'Customer Tracking', description: 'Branded tracking with ETAs powered by the same live model as your tower' },
        { title: 'Analytics & Reports', description: 'See what agents and drivers moved—and where automation recovered time and margin' },
      ],
    },
    transformation: {
      badge: 'Transformation',
      title: 'What Geofleet does for your',
      titleHighlight: 'logistics operations',
      subtitle: 'From manual, fragmented workflows to an AI-powered control tower — see the shift.',
      withoutGeofleet: 'Without Geofleet',
      withGeofleet: 'With Geofleet',
      controlTower: 'AI-powered control tower',
      exploreFeatures: 'Explore features',
      beforeItems: [
        'Hours spent on manual dispatch and route planning',
        'No real-time visibility — customers and ops in the dark',
        "Spreadsheets and multiple tools that don't talk to each other",
        'Late deliveries, failed proof of delivery, and disputes',
        'Revenue leakage from COD, refunds, and inefficiency',
      ],
      afterItems: [
        'AI agents dispatch and optimize routes in seconds',
        'Live tracking, ETAs, and branded tracking pages for customers',
        'One control tower for fleet, orders, and analytics',
        'ePOD, e-signature, OTP — fewer disputes, audit-ready proof',
        'Recover revenue, cut costs, and scale with confidence',
      ],
      joinTeams: "Join teams who've made the shift",
      stat1: '20–30% cost reduction',
      stat2: '95% on-time delivery',
      stat3: 'Zero manual dispatch',
    },
    scaleImpact: {
      title: 'Fleet operations run on',
      titleBrand: 'Geofleet',
      subtitle: 'From single-hub teams to multi-country fleets—AI agents and humans in one last-mile command centre.',
      stats: [
        { label: 'On-time delivery rate' },
        { label: 'Cost reduction' },
        { label: 'Driver efficiency' },
        { label: 'AI-powered operations' },
      ],
      multiRegion: 'Multi-region',
      enterpriseReady: 'Enterprise-ready',
      integrations: '50+ integrations',
    },
    trustedLeaders: {
      badge: 'Trusted by Industry Leaders',
      title: 'Enterprise teams run on',
      titleBrand: 'Geofleet',
      subtitle: 'From high-growth startups to Fortune 500 logistics — businesses processing millions of deliveries trust Geofleet to power their operations.',
      testimonials: [
        { quote: "Geofleet's AI dispatch eliminated most of our manual routing. We scaled daily prescription deliveries without adding dispatchers—compliance and on-time delivery improved across the board.", author: 'Operations Lead', role: 'Pharmacy', company: 'Qatar', metric: 'Scaled deliveries' },
        { quote: 'The predictive ETA engine reduced customer complaint calls significantly. Our enterprise clients now consider us best-in-class for last-mile delivery in the region.', author: 'Head of Logistics', role: 'Logistics Delivery Company', company: 'UAE', metric: '70% fewer complaints' },
        { quote: 'We integrated Geofleet across our field teams in weeks. The platform handles dispatch, driver visibility, and proof of delivery so we can focus on uptime and safety.', author: 'Fleet Manager', role: 'Renewable Energy Company', company: 'India', metric: 'Faster rollout' },
      ],
      stats: [
        { value: '100+', label: 'Clients' },
        { value: '1M+', label: 'Deliveries Processed' },
        { value: '20+', label: 'Countries' },
        { value: '99.99%', label: 'Platform Uptime' },
        { value: '24/7', label: 'Enterprise Support' },
      ],
      badges: [
        { label: 'SOC 2 Compliant' },
        { label: 'GDPR Ready' },
        { label: '99.99% SLA' },
        { label: 'SSO' },
        { label: 'API-First' },
      ],
    },
    driverExperience: {
      badge: 'Driver Experience',
      title: "A driver app they'll",
      titleHighlight: 'actually love',
      subtitle: "Built for speed and simplicity. Everything a driver needs to complete deliveries faster — nothing they don't.",
      features: [
        { title: 'AI-optimized routes', description: 'Turn-by-turn navigation with real-time traffic rerouting. Drivers always take the fastest path.', stat: '30% faster' },
        { title: 'Smart proof of delivery', description: 'AI-verified photos, e-signatures, and OTP confirmation — all captured in one tap.', stat: '1-tap POD' },
        { title: 'Intelligent task queue', description: 'AI sequences stops by priority, time-windows, and proximity. No guesswork, no missed SLAs.', stat: '95% on-time' },
        { title: 'In-app communication', description: 'One-tap calls, masked numbers for privacy, and dispatcher chat — all without leaving the app.', stat: 'Zero friction' },
        { title: 'Barcode & ID scan', description: 'Scan package barcodes and verify customer IDs for age-restricted or high-value deliveries.', stat: '100% verified' },
        { title: 'Offline-ready', description: 'Full functionality in dead zones. Data syncs automatically when connectivity is restored.', stat: 'Always on' },
      ],
      delivered: 'Delivered',
      orderId: '#1246',
      rating: 'Rating',
      ratingValue: '4.9/5',
      availableOn: 'Available on iOS and Android',
      downloadOnThe: 'Download on the',
      appStore: 'App Store',
      getItOn: 'Get it on',
      googlePlay: 'Google Play',
      appStoreRating: '4.9 on App Store',
    },
    coreFeatures: {
      badge: 'AI + platform',
      title: 'See Geofleet in action',
      subtitle: 'Dispatch, agentic routing, tracking, and analytics—everything your agents and teams need in one stack.',
      platformLine: 'Product depth plus enterprise visibility—agents and people working from the same live data.',
      carousel: {
        dispatch: 'Dispatch',
        routes: 'Routes',
        tracking: 'Tracking',
        analytics: 'Analytics',
        dispatchHeadline: 'One hub for every order and driver',
        dispatchDescription: 'Manage dispatch from a single screen — assign drivers, update status, and handle exceptions in real time. No more spreadsheets or missed orders.',
        routesHeadline: 'AI-optimized routes in seconds',
        routesDescription: 'Our engine factors in traffic, time windows, and capacity to cut miles and fuel. Deliver more stops per run with less planning.',
        trackingHeadline: 'Branded tracking, live map & ETA',
        trackingDescription: 'Give every customer a tracking page with your logo and a live map. Fewer "where is my order?" calls and happier reviews.',
        analyticsHeadline: 'Insights that drive better performance',
        analyticsDescription: 'Delivery times, on-time rates, driver scorecards, and cost-per-drop — all in dashboards that help you improve every week.',
      },
      learnMore: 'Learn more',
      whatIncluded: "What's included",
      stats: { eta: 'ETA accuracy', savings: 'Route savings', disputes: 'Fewer disputes' },
      ctaDemo: 'Book a demo',
      ctaTrial: 'Get started',
      pillarsCrossLink:
        'Command centre & Copilot are covered in How it works above — this carousel is the four core product surfaces.',
      pillarsCrossLinkCta: 'Jump to How it works',
    },
    integrations: {
      label: 'Integrations',
      title: 'Model Context Protocol for your delivery stack',
      paragraph:
        'Geofleet supports MCP so assistants and agents can work with structured, permissioned context from your operations—alongside native connectors for 10+ SMS integrations, 10+ email providers, 10+ payment gateways, 5,000+ IoT devices, and 10+ OMS like Shopify and Unicommerce. One protocol for AI; full coverage for SMS, email, payments, IoT, and order systems.',
      learnMore: 'Learn more',
      footerLine: 'MCP · SMS · Email · WhatsApp · Line · Payments · OMS · IoT · POS · Delivery',
    },
    demoCta: {
      title: 'Put AI agents to work on your logistics',
      subtitle: 'Join 100+ businesses running dispatch, routing, and exceptions from one Geofleet command centre',
      ctaTrial: 'Get started',
      ctaDemo: 'Book a demo',
      noCard: 'Lite from $49/month, 7-day free trial',
      freeTrial: 'Command from $190/month for 10 drivers',
      cancelAnytime: 'Cancel anytime',
    },
    faq: {
      supportLabel: 'Support',
      title: 'Frequently asked questions',
      subtitle: 'Quick answers about Geofleet and how to get started.',
      items: [
        { question: 'What is Geofleet?', answer: 'Geofleet is an AI-powered delivery and fleet management platform with AI agents inside the command centre. It automates carrier and driver allocation, agentic route planning, SLA alerts, and exception workflows—while giving you real-time tracking, predictive ETAs, ePOD, and analytics across hubs, drivers, and integrations.' },
        { question: 'How do I get started?', answer: 'If you deliver from one hub, start with Geofleet Lite from $49 a month for 3 drivers and 500 orders, with a 7-day free trial. Lite Plus ($99) adds route planning, auto-allocation, WhatsApp automation and API, and Lite Autopilot ($179) adds AI Copilot and Autopilot. Multi-hub teams start on Geofleet Command from $190 a month for 10 drivers. You can connect your store, add drivers and start dispatching in minutes.' },
        { question: 'Does Geofleet work with my existing tools?', answer: 'Yes. Geofleet integrates with 10+ SMS providers, 10+ email providers, 10+ payment gateways, 5,000+ IoT devices, and 10+ OMS like Shopify and Unicommerce, plus POS and delivery partners. We also offer an API and webhooks for custom integrations. See the Integrations page for the full list.' },
        { question: 'How much does Geofleet cost?', answer: 'Geofleet Lite starts at $49 a month for 3 drivers and 500 orders from one hub, with Lite Plus at $99 and Lite Autopilot at $179. Geofleet Command starts at $190 a month for 10 drivers and 5,000 orders on Professional, which is $19 per driver; Premium AI is $29 per driver. Enterprise is custom.' },
        { question: 'Do you support multiple locations and hubs?', answer: 'Yes. Geofleet is built for multi-hub and multi-region operations. You can manage multiple warehouses, dark kitchens, or stores from one dashboard, with role-based access, hub-level analytics, and AI dispatch that considers all locations.' },
        { question: 'What kind of support do you offer?', answer: 'We provide email support, in-app help, and documentation for all plans. Higher tiers include priority support and dedicated success managers. You can also book a demo or talk to sales for custom requirements and enterprise needs.' },
      ],
    },
  },
};
